import { useState, useEffect } from "react";
import axios from "axios";
import "@/styles/fj.css";
import "@/styles/commandDeck.css";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import JobDetailsModal from "@/components/JobDetailsModal";
import AdvancedJobDetailsModalForCrew from "@/components/AdvancedJobDetailsModalForCrew";
import JobDetailsCrewModal from "@/components/JobDetailsCrewModal";
import ProfileBlockerModal from "@/components/ProfileBlockerModal";
import CancellationPaymentBlockerModal from "@/components/CancellationPaymentBlockerModal";
import { toast } from "sonner";
import {
  TrendingUp,
  MapPin,
  LocateFixed,
  Calendar,
  Flame,
  Shield,
  Clock,
  ChevronDown,
  X,
  Briefcase,
  Check,
  Send,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { loadGoogleMapsScript } from '@/utils/googleMapsLoader';
import { initPlaceAutocomplete, destroyPlaceAutocomplete } from '@/utils/placeAutocomplete';
import {
  roleMeta,
  roleColor,
  money,
  initial,
  avatarGradient,
  isAdvancedJob as isAdvancedJobFn,
  advancedTotal,
} from "@/utils/fjMeta";

const FindJobsRedesigned = ({ onLogout }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [fullJobData, setFullJobData] = useState(null);
  const [activeTab, setActiveTab] = useState('available'); // 'available' or 'archived'
  const [locationSearch, setLocationSearch] = useState('');
  const [radiusKm, setRadiusKm] = useState(300);
  const [userLocation, setUserLocation] = useState(null);
  const [nearMeEnabled, setNearMeEnabled] = useState(false); // "Jobs Near Me" toggle
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobsPage, setJobsPage] = useState(1);
  const [hasMoreJobs, setHasMoreJobs] = useState(true);
  const [selectedJobType, setSelectedJobType] = useState('all'); // Job category filter (Weddings, Events, etc.)
  const [selectedRole, setSelectedRole] = useState('all'); // Required role filter (Photographer, Videographer, etc.)

  // Apply Modal states
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [notifyIfUnsuccessful, setNotifyIfUnsuccessful] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [showProfileBlocker, setShowProfileBlocker] = useState(false);
  const [userStatus, setUserStatus] = useState('');
  const [outstandingCancellationFees, setOutstandingCancellationFees] = useState(null);
  const [showCancellationBlocker, setShowCancellationBlocker] = useState(false);
  const [selectedApplyRole, setSelectedApplyRole] = useState(null); // For advanced job role selection

  // Fetch user status from database on mount
  useEffect(() => {
    const fetchUserStatus = async () => {
      if (user.id) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${user.id}`);
          const dbStatus = (response.data.status || '').toUpperCase();
          console.log('📊 FindJobs - User status from DB:', dbStatus);
          setUserStatus(dbStatus);
        } catch (error) {
          console.error('Error fetching user status:', error);
        }
      }
      // Check for outstanding cancellation fees
      if (user.email) {
        try {
          const feesResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/user/outstanding-cancellation-fees`,
            { params: { user_email: user.email } }
          );
          if (feesResponse.data.has_outstanding_fees) {
            setOutstandingCancellationFees(feesResponse.data);
            setShowCancellationBlocker(true);
          }
        } catch (err) {
          console.error('Failed to check cancellation fees:', err);
        }
      }
    };
    fetchUserStatus();
  }, [user.id]);

  // Load user's location from profile on mount
  useEffect(() => {
    const loadUserLocation = async () => {
      if (user.id) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${user.id}`);
          if (response.data.location_lat && response.data.location_lon) {
            setUserLocation({
              lat: response.data.location_lat,
              lon: response.data.location_lon,
              city: response.data.location_city,
              state: response.data.location_state
            });
          }
        } catch (error) {
          console.error('Failed to load user location:', error);
        }
      }
    };

    // Load Google Maps for location autocomplete
    loadGoogleMapsScript(() => {
      setTimeout(() => {
        initPlaceAutocomplete(
          'find-jobs-location-search',
          (place) => {
            setLocationSearch(place.formatted_address);
            if (place.lat && place.lng) {
              const coords = {
                lat: place.lat,
                lon: place.lng
              };
              setUserLocation(coords);
              fetchJobs(1, { locationOverride: coords });
            }
          },
          {
            // Transparent input inside the styled field; inherits .fj-root color
            // (var(--fj-text)) so typed text is readable in both light and dark.
            className: 'w-full bg-transparent border-none outline-none',
            placeholder: 'Search a suburb or city',
          }
        );
      }, 100);
    });

    loadUserLocation();

    return () => {
      destroyPlaceAutocomplete('find-jobs-location-search');
    };
  }, [user.id]);

  useEffect(() => {
    fetchJobs(1);
  }, []);

  const fetchJobs = async (page = 1, options = {}) => {
    try {
      setLoading(true);

      // Build query params
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/jobs/available?user_email=${user.email || 'test@shootzu.com'}&page=${page}&limit=50`;

      // Add location params if "Near Me" enabled or manual location search
      const loc = options.locationOverride || userLocation;
      const rad = options.radius || radiusKm;

      if (!options.skipLocationFilter && nearMeEnabled && loc) {
        // Use user's profile location for "Near Me" feature
        url += `&location_lat=${loc.lat}&location_lon=${loc.lon}&radius_km=${rad}`;
        console.log('Near Me search:', { lat: loc.lat, lon: loc.lon, radius: rad });
      } else if (!options.skipLocationFilter && loc && rad < 9999 && (options.locationOverride || locationSearch)) {
        // Use manually searched location (or override from autocomplete callback)
        url += `&location_lat=${loc.lat}&location_lon=${loc.lon}&radius_km=${rad}`;
        console.log('Location search:', { lat: loc.lat, lon: loc.lon, radius: rad, location: locationSearch });
      }

      const response = await axios.get(url);

      const rawJobs = response.data.jobs || [];

      // Map backend response directly - no additional API calls needed
      const newJobs = rawJobs.map(job => {
        // Ensure job type is set with fallback
        const jobType = job.job_type || job.type || 'photographer';
        const requiredRoles = job.required_roles || [];

        // Check if this is an advanced job with multiple positions
        const crewMembers = job.crew_members || job.crewMembers || [];
        const isAdvancedJob = crewMembers.length > 1;

        return {
          id: job.id,
          jobNumber: job.job_number,
          title: job.title,
          brief: job.description || '',
          jobDate: job.date,
          ratePerHour: job.pay_rate || 0,
          hours: job.hours || 0,
          total: job.total_amount || (job.pay_rate * job.hours) || 0,
          location: job.location_address || '',
          distanceKm: job.distance_km, // Distance from user's location
          type: jobType,
          requiredRoles: requiredRoles,
          status: job.status,
          poster_id: job.poster_id, // For ownership check
          requires_wwcc: job.requires_wwcc || false, // WWCC requirement
          crew_members: crewMembers, // For advanced job detection
          isAdvancedJob: isAdvancedJob, // Quick check for multi-position jobs
          applicantCount: job.applicant_count || 0,
          isPopular: (job.applicant_count || 0) >= 5,
          owner: {
            name: job.owner_name || 'Unknown',
            id: job.owner_id,
            photo: job.owner_photo_url || 'https://via.placeholder.com/150',
            featurePhoto: job.owner_feature_photo_url, // First portfolio image
            email: job.owner_email
          }
        };
      });

      if (page === 1) {
        setAvailableJobs(newJobs);
      } else {
        setAvailableJobs(prev => [...prev, ...newJobs]);
      }

      setJobsPage(page);
      setHasMoreJobs(newJobs.length === 50);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
      if (page === 1) setAvailableJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreJobs = () => {
    fetchJobs(jobsPage + 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const dayOfWeek = date.toLocaleString('en-US', { weekday: 'short' });

    return {
      formatted: `${day} ${month} ${year}`,
      day: day,
      month: month,
      year: year,
      dayOfWeek: dayOfWeek,
      toString: function() { return this.formatted; }
    };
  };

  const formatRoles = (roles) => {
    if (!roles || roles.length === 0) return '';
    const roleLabels = {
      'photographer': 'Photographer',
      'videographer': 'Videographer',
      'editor': 'Editor',
      'editor_photos': 'Editor (Photos)',
      'editor_video': 'Editor (Video)',
      'assistant': 'Assistant',
      'drone_operator': 'Drone Operator',
      'voxpop_videographer': 'VoxPop Videographer'
    };
    return roles.map(r => roleLabels[r] || r).join(', ');
  };

  const getCrewPayout = (amount) => {
    return Math.floor(amount * 0.98);
  };

  const filteredJobs = availableJobs
    .filter(job => {
      // Exclude HIRED jobs from Find Jobs (they should only show in Hire Crew for job owner)
      if (job.status === 'HIRED' || job.status === 'PENDING_COMPLETION' || job.status === 'PAID_COMPLETED') {
        return false;
      }

      // Exclude past jobs - only show future jobs
      const jobDate = new Date(job.jobDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      jobDate.setHours(0, 0, 0, 0);
      if (jobDate < today) {
        return false;
      }

      // Filter by search
      const matchesSearch = searchQuery === '' ||
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.jobNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.location.toLowerCase().includes(searchQuery.toLowerCase());

      // Filter by job category (WEDDINGS, EVENTS, CORPORATE, etc.)
      const matchesJobType = selectedJobType === 'all' || job.type.toLowerCase() === selectedJobType.toLowerCase();

      // Filter by required role (photographer, videographer, editor, etc.)
      // Check requiredRoles first, then fall back to crew_members roles
      const matchesRole = selectedRole === 'all' ||
        (job.requiredRoles && job.requiredRoles.length > 0 && job.requiredRoles.some(role => role.toLowerCase() === selectedRole.toLowerCase())) ||
        (job.crew_members && job.crew_members.length > 0 && job.crew_members.some(member => member.role && member.role.toLowerCase() === selectedRole.toLowerCase()));

      // No tab filtering - show all non-archived jobs
      // Only exclude CANCELLED jobs
      const isArchived = job.status === 'CANCELLED';
      if (isArchived) {
        return false;
      }

      return matchesSearch && matchesJobType && matchesRole;
    })
    .sort((a, b) => {
      // If Near Me enabled, sort by distance
      if (nearMeEnabled && a.distanceKm !== undefined && b.distanceKm !== undefined) {
        return a.distanceKm - b.distanceKm;
      }
      // Otherwise sort by next occurring date (soonest first)
      const dateA = new Date(a.jobDate);
      const dateB = new Date(b.jobDate);
      return dateA - dateB;
    });

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    fetchFullJobDetails(job.id);
  };

  const isMyJob = (job) => {
    // Check if current user is the job owner
    return job.poster_id === user.id || job.owner?.email === user.email || job.owner_id === user.id;
  };

  const fetchFullJobDetails = async (jobId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/details/${jobId}`
      );
      setFullJobData(response.data);
      setShowJobDetails(true);
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      toast.error('Failed to load job details');
    }
  };

  const handleApplyNow = (job) => {
    // Block NEW and PENDING_APPROVAL users (using database status)
    if (userStatus === 'NEW' || userStatus === 'PENDING_APPROVAL') {
      console.log('🚫 Blocking apply for status:', userStatus);
      setShowJobDetails(false);
      setShowProfileBlocker(true);
      return;
    }

    // Check if user is the job owner
    const jobData = fullJobData || job;
    const isJobOwner = jobData?.owner?.id === user.id ||
                       jobData?.owner?.email === user.email ||
                       jobData?.owner_id === user.id ||
                       jobData?.client_email === user.email;
    if (isJobOwner) {
      toast.error("You cannot apply to your own job");
      setShowJobDetails(false);
      return;
    }

    // Close job details and open apply modal
    setShowJobDetails(false);
    setShowApplyModal(true);
    // Reset selected role for advanced jobs - user must select one
    setSelectedApplyRole(null);
    // fullJobData is already set from job details
  };

  const handleSubmitApplication = async () => {
    if (!fullJobData) return;

    // Validate owner email is available
    if (!fullJobData.owner?.email) {
      toast.error('Unable to submit application - job owner information is not available');
      return;
    }

    // For advanced jobs with multiple positions, require a role selection
    const crewMembers = fullJobData.crew_members || [];
    const isAdvancedJob = crewMembers.length > 1;
    if (isAdvancedJob && !selectedApplyRole) {
      toast.error('Please select a role to apply for');
      return;
    }

    setSubmittingApplication(true);

    try {
      const applicantName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown';

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/apply`,
        {
          job_id: fullJobData.id,
          job_title: fullJobData.title,
          applicant_name: applicantName,
          applicant_email: user.email,
          application_message: applicationMessage,
          notify_if_unsuccessful: notifyIfUnsuccessful,
          job_owner_name: fullJobData.owner?.name || fullJobData.owner_name || 'Job Owner',
          job_owner_email: fullJobData.owner?.email || fullJobData.owner_email,
          job_date: fullJobData.date || fullJobData.jobDate || 'TBD',
          role: selectedApplyRole || null // Include selected role for advanced jobs
        }
      );

      if (response.data) {
        // Close apply modal and show confirmation
        setShowApplyModal(false);
        setShowConfirmationModal(true);
        setApplicationMessage('');
        setNotifyIfUnsuccessful(false);
        setSelectedApplyRole(null);
      }
    } catch (error) {
      console.error('Failed to submit application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setSubmittingApplication(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmationModal(false);
    // Navigate to My Applications page
    navigate('/applications');
  };

  const handleAskQuestion = async (job) => {
    // The job-details payload exposes the owner under several possible shapes.
    const ownerEmail =
      job.owner?.email || job.owner_email || job.poster_email ||
      job.client_email || job.user_email || job.created_by_email;
    const ownerName =
      job.owner?.name || job.owner_name || job.poster_name ||
      job.client_name || 'the job owner';

    if (!ownerEmail) {
      toast.error('Unable to contact the job owner — their contact is not available yet.');
      return;
    }
    if (ownerEmail === user.email) {
      toast.error("This is your own job.");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversation/create`,
        {
          job_id: job.id,
          job_title: job.title || job.job_title,
          user1_email: user.email,
          user2_email: ownerEmail,
        }
      );

      const conversationId = response.data?.conversation_id || response.data?.conversation?.id || response.data?.id;
      setShowJobDetails(false);
      toast.success(`Opening conversation with ${ownerName}…`);
      navigate('/messages', conversationId ? { state: { conversationId, autoOpen: true } } : undefined);
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    }
  };

  // ---- Derived display helpers (Scan) ----

  // Roles to show as chips for a job (prefer required_roles, fall back to crew roles)
  const jobRoles = (job) => {
    if (job.requiredRoles && job.requiredRoles.length > 0) return job.requiredRoles;
    if (job.crew_members && job.crew_members.length > 0) {
      return [...new Set(job.crew_members.map(m => m.role).filter(Boolean))];
    }
    return [job.type].filter(Boolean);
  };

  // Per-role count for the filter pills (counts jobs whose roles include the role)
  const roleCount = (roleKey) => {
    if (roleKey === 'all') return filteredJobs.length;
    return filteredJobs.filter(job =>
      jobRoles(job).some(r => (r || '').toLowerCase() === roleKey)
    ).length;
  };

  // Short location = last 2 comma-separated segments
  const shortLocation = (loc) => {
    if (!loc) return '';
    const parts = loc.split(',').map(s => s.trim()).filter(Boolean);
    return parts.slice(-2).join(', ');
  };

  const ROLE_PILLS = [
    { key: 'all', label: 'All' },
    { key: 'photographer', label: 'Photographer' },
    { key: 'videographer', label: 'Videographer' },
    { key: 'editor', label: 'Editor' },
    { key: 'assistant', label: 'Assistant' },
    { key: 'drone_operator', label: 'Drone Op' },
  ];

  // Avatar element with image + initial fallback (Command Deck gradient tile)
  const Avatar = ({ name, photo, size = 54, radius = 14 }) => {
    const valid = photo && photo !== 'https://via.placeholder.com/150';
    return (
      <div
        className="cd-display"
        style={{
          width: size, height: size, borderRadius: radius, flex: '0 0 auto',
          background: avatarGradient(name), color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 17, overflow: 'hidden',
        }}
      >
        {valid ? (
          <img
            src={photo}
            alt={name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : initial(name)}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="cd-main">

          {/* ===== Header ===== */}
          <div className="cd-col-mobile" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 22 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#28C0D0' }}>BROWSE WORK</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11.5, color: '#67C6B4', background: 'rgba(103,198,180,.16)', padding: '4px 11px', borderRadius: 999 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 999, background: '#67C6B4' }} />
                  {filteredJobs.length} live now
                </span>
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, letterSpacing: '-1px', color: '#fff', marginTop: 8 }}>Find your next gig</div>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,.6)', lineHeight: 1.5, maxWidth: '42ch', marginTop: 8 }}>
                Paid photo &amp; video work near you — apply in seconds, get hired, get paid.
              </p>
            </div>
          </div>

          {/* ===== Search + filters card ===== */}
          <div style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '18px 20px', backdropFilter: 'blur(4px)' }}>
            <div className="cd-col-mobile" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Location search (Google Places autocomplete mounts into this div) */}
              <div style={{ position: 'relative', flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', border: '1px solid rgba(255,255,255,.12)', borderRadius: 12, background: 'rgba(255,255,255,.04)', color: '#fff' }}>
                <MapPin className="w-[18px] h-[18px] flex-shrink-0" style={{ color: '#28C0D0' }} />
                <div id="find-jobs-location-search" className="hero-autocomplete" style={{ flex: 1, minWidth: 0, color: '#fff' }} />
              </div>

              {/* Jobs near me toggle */}
              {userLocation && (
                <button
                  onClick={() => {
                    const willEnable = !nearMeEnabled;
                    setNearMeEnabled(willEnable);
                    setLocationSearch('');
                    if (willEnable && userLocation) {
                      fetchJobs(1, { locationOverride: userLocation });
                    } else {
                      fetchJobs(1, { skipLocationFilter: true });
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', borderRadius: 12,
                    background: nearMeEnabled ? 'rgba(40,192,208,.12)' : 'rgba(255,255,255,.04)',
                    border: nearMeEnabled ? '1px solid rgba(40,192,208,.3)' : '1px solid rgba(255,255,255,.12)',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>
                    {nearMeEnabled && userLocation.city ? `Near ${userLocation.city}` : 'Jobs near me'}
                  </span>
                  <span style={{ width: 38, height: 22, borderRadius: 999, background: nearMeEnabled ? '#28C0D0' : 'rgba(255,255,255,.18)', position: 'relative', flex: '0 0 auto', transition: 'background .15s' }}>
                    <span style={{ position: 'absolute', top: 2, [nearMeEnabled ? 'right' : 'left']: 2, width: 18, height: 18, borderRadius: 999, background: nearMeEnabled ? '#0C1120' : '#fff' }} />
                  </span>
                </button>
              )}

              {/* Radius control */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 12, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.12)', color: '#fff', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                <Clock className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,.6)' }} />
                <span>Within</span>
                <select
                  value={radiusKm}
                  onChange={(e) => {
                    const newRadius = parseInt(e.target.value); setRadiusKm(newRadius);
                    fetchJobs(1, { radius: newRadius });
                  }}
                  style={{ background: 'transparent', outline: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#fff', fontFamily: 'inherit' }}
                >
                  <option value="50" style={{ color: '#0C1120' }}>50 km</option>
                  <option value="100" style={{ color: '#0C1120' }}>100 km</option>
                  <option value="200" style={{ color: '#0C1120' }}>200 km</option>
                  <option value="300" style={{ color: '#0C1120' }}>300 km</option>
                  <option value="500" style={{ color: '#0C1120' }}>500 km</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 -ml-1" style={{ color: 'rgba(255,255,255,.6)' }} />
              </div>

              {locationSearch && (
                <button
                  onClick={() => {
                    setLocationSearch('');
                    setUserLocation(null);
                    fetchJobs(1, { skipLocationFilter: true });
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 14px', borderRadius: 12, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.12)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  <X className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>

            {/* Role filter pills with live counts */}
            <div style={{ display: 'flex', gap: 9, marginTop: 16, flexWrap: 'wrap' }}>
              {ROLE_PILLS.map((p) => {
                const active = selectedRole === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setSelectedRole(p.key)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px', borderRadius: 999,
                      fontFamily: '"Work Sans", sans-serif', fontSize: 13, cursor: 'pointer',
                      background: active ? '#28C0D0' : 'rgba(255,255,255,.05)',
                      border: active ? 'none' : '1px solid rgba(255,255,255,.12)',
                      color: active ? '#0C1120' : 'rgba(255,255,255,.78)',
                      fontWeight: active ? 700 : 600,
                    }}
                  >
                    {p.label}
                    <span style={{ fontWeight: 700, opacity: active ? 0.7 : 0.5 }}>{roleCount(p.key)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ===== Jobs list ===== */}
          {loading ? (
            /* Loading skeletons — 6 rows */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginTop: 18 }}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="cd-skel">
                  <span style={{ width: 54, height: 54, borderRadius: 14, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ width: 120, height: 11, borderRadius: 6, background: 'rgba(255,255,255,.08)', marginBottom: 10 }} />
                    <div style={{ width: '60%', height: 16, borderRadius: 6, background: 'rgba(255,255,255,.1)', marginBottom: 10 }} />
                    <div style={{ width: '40%', height: 10, borderRadius: 6, background: 'rgba(255,255,255,.07)' }} />
                  </div>
                  <div style={{ width: 70, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.08)', flex: '0 0 auto' }} />
                </div>
              ))}
            </div>
          ) : filteredJobs.length === 0 ? (
            /* Empty state */
            <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 18 }}>
              <span style={{ width: 64, height: 64, borderRadius: 18, background: 'rgba(40,192,208,.12)', color: '#28C0D0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
                <Briefcase className="w-[30px] h-[30px]" />
              </span>
              <div className="cd-display" style={{ fontWeight: 700, fontSize: 19, color: '#fff' }}>No jobs match your filters</div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.55)', marginTop: 7, maxWidth: 340, lineHeight: 1.55 }}>
                Try widening your radius or clearing a role filter — new gigs are posted every day.
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginTop: 18 }}>
                {filteredJobs.map((job) => {
                  const advanced = job.isAdvancedJob || isAdvancedJobFn(job);
                  const mine = isMyJob(job);
                  const roles = jobRoles(job);
                  const rolesShown = roles.slice(0, 2);
                  const moreRoles = roles.length - rolesShown.length;
                  const advTotal = advancedTotal(job);
                  const rolesOpen = (job.crew_members || []).filter(m => !m.hired).length;
                  const df = formatDate(job.jobDate);

                  return (
                    <div
                      key={job.id}
                      className="cd-col-mobile"
                      onClick={() => handleViewDetails(job)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 18,
                        background: mine ? 'rgba(40,192,208,.05)' : 'rgba(255,255,255,.045)',
                        border: mine ? '1px solid rgba(40,192,208,.2)' : '1px solid rgba(255,255,255,.09)',
                        borderRadius: 16, padding: '18px 20px', cursor: 'pointer',
                        transition: 'transform .14s, border-color .14s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = mine ? 'rgba(40,192,208,.4)' : 'rgba(40,192,208,.35)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = mine ? 'rgba(40,192,208,.2)' : 'rgba(255,255,255,.09)'; }}
                    >
                      {/* Avatar + middle stay on one row (avatar | title) on mobile */}
                      <div className="cd-fj-top">
                      {/* Avatar */}
                      <Avatar name={job.owner.name} photo={job.owner.photo} />

                      {/* Middle */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Meta row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginBottom: 6 }}>
                          <span className="cd-mono" style={{ fontSize: 11.5, color: 'rgba(255,255,255,.4)' }}>
                            {job.jobNumber || (job.id || '').slice(0, 8).toUpperCase()}
                          </span>
                          {rolesShown.map((r, idx) => (
                            <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,.08)', padding: '3px 9px', borderRadius: 999 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 999, background: roleColor(r) }} />
                              {roleMeta(r).label}
                            </span>
                          ))}
                          {moreRoles > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.6)', background: 'rgba(255,255,255,.05)', padding: '3px 9px', borderRadius: 999 }}>+{moreRoles} more</span>
                          )}
                          {advanced && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#C98AD4', background: 'rgba(120,40,124,.25)', padding: '3px 9px', borderRadius: 999 }}>Multi-role</span>
                          )}
                          {mine && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#28C0D0', background: 'rgba(40,192,208,.16)', padding: '3px 9px', borderRadius: 999 }}>Your job</span>
                          )}
                          {job.isPopular && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#FCD34D', background: 'rgba(180,83,9,.22)', padding: '3px 9px', borderRadius: 999 }}>
                              <Flame className="w-3 h-3" fill="currentColor" stroke="none" />
                              Popular
                            </span>
                          )}
                        </div>

                        {/* Title */}
                        <div
                          className="cd-display cd-fj-title"
                          style={{ fontWeight: 700, fontSize: 18, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {job.title}
                        </div>

                        {/* Sub-meta */}
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 18, marginTop: 8, fontSize: 13, color: 'rgba(255,255,255,.6)' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Calendar className="w-3.5 h-3.5" />
                            {df === 'TBD' ? 'TBD' : `${df.dayOfWeek}, ${df.formatted}`}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <MapPin className="w-3.5 h-3.5" />
                            {shortLocation(job.location) || 'Location TBD'}
                          </span>
                          {nearMeEnabled && job.distanceKm !== undefined && job.distanceKm !== null && (
                            <span style={{ color: '#28C0D0', fontWeight: 600 }}>{job.distanceKm} km away</span>
                          )}
                          {job.requires_wwcc && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#67C6B4', fontWeight: 600 }}>
                              <Shield className="w-3 h-3" />
                              WWCC
                            </span>
                          )}
                        </div>
                      </div>
                      </div>

                      {/* Price + action collapse into one row on mobile */}
                      <div className="cd-card-actions">
                      {/* Payment block */}
                      <div className="cd-card-pay" style={{ textAlign: 'right', flex: '0 0 auto' }}>
                        {advanced ? (
                          <>
                            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>Up to</div>
                            <div className="cd-display" style={{ fontWeight: 800, fontSize: 26, color: '#67C6B4', letterSpacing: '-.6px', lineHeight: 1.1 }}>
                              {money(getCrewPayout(advTotal))}
                            </div>
                            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>
                              {rolesOpen} {rolesOpen === 1 ? 'role' : 'roles'} open
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>You earn</div>
                            <div className="cd-display" style={{ fontWeight: 800, fontSize: 26, color: '#67C6B4', letterSpacing: '-.6px', lineHeight: 1.1 }}>
                              {money(getCrewPayout(job.total))}
                            </div>
                            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>
                              {money(getCrewPayout(job.ratePerHour))}/hr · {job.hours}h
                            </div>
                          </>
                        )}
                      </div>

                      {/* Action button — opens details (preserves existing apply flow) */}
                      <button
                        className="cd-card-act"
                        onClick={(e) => { e.stopPropagation(); handleViewDetails(job); }}
                        style={{
                          padding: '12px 22px', borderRadius: 12, flex: '0 0 auto', cursor: 'pointer',
                          fontWeight: mine ? 600 : 700, fontSize: 13.5,
                          background: mine ? 'rgba(255,255,255,.06)' : '#28C0D0',
                          border: mine ? '1px solid rgba(255,255,255,.16)' : 'none',
                          color: mine ? '#fff' : '#0C1120',
                        }}
                      >
                        {mine ? 'Manage' : advanced ? 'View roles' : 'Apply'}
                      </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Load more */}
              {hasMoreJobs && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                  <button onClick={loadMoreJobs} style={{ padding: '13px 28px', borderRadius: 12, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.14)', color: '#fff', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>
                    Load more jobs
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ===== Job Details Modal (multi-role) ===== */}
        {showJobDetails && fullJobData && (
          <JobDetailsCrewModal
            job={fullJobData}
            isOpen={showJobDetails}
            onClose={() => {
              setShowJobDetails(false);
              setFullJobData(null);
            }}
            onApplyRole={(role) => {
              // Reuse the existing apply flow (NEW/PENDING gating + own-job check),
              // scoped to the chosen role. handleApplyNow resets selectedApplyRole,
              // so set it afterwards (same render batch → our value wins).
              handleApplyNow(fullJobData);
              setSelectedApplyRole(role || null);
            }}
            onAskQuestion={!isMyJob(fullJobData) ? () => handleAskQuestion(fullJobData) : undefined}
          />
        )}

        {/* ===== Apply flow (inline modal) ===== */}
        {showApplyModal && fullJobData && (() => {
          const crewMembers = fullJobData.crew_members || [];
          const isAdvancedApply = crewMembers.length > 1;
          const dateStr = fullJobData.date || fullJobData.jobDate;
          let dateLabel = 'Date TBD';
          if (dateStr && dateStr !== 'TBD') {
            const d = new Date(dateStr);
            dateLabel = `${String(d.getDate()).padStart(2, '0')} ${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()}`;
          }

          return (
            <div className="cd-modal-overlay">
              <div className="cd-modal-card" style={{ maxWidth: 520 }}>
                {/* Header band */}
                <div style={{ position: 'relative', background: isAdvancedApply ? 'linear-gradient(120deg, #572B7C, #78287C)' : 'linear-gradient(120deg, rgba(87,43,124,.6), rgba(120,40,124,.34))', borderBottom: '1px solid rgba(255,255,255,.1)', borderRadius: '22px 22px 0 0', padding: '24px 26px', color: '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#28C0D0' }}>APPLY TO JOB</div>
                      <h3 className="cd-display" style={{ fontWeight: 800, fontSize: 22, color: '#fff', letterSpacing: '-.5px', lineHeight: 1.2, marginTop: 6 }}>
                        {fullJobData.title}
                      </h3>
                      <p style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', marginTop: 4 }}>
                        {dateLabel}{fullJobData.location ? ` · ${shortLocation(fullJobData.location)}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => { setShowApplyModal(false); setShowJobDetails(true); }}
                      className="cd-modal-close"
                      style={{ position: 'static' }}
                      aria-label="Close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {isAdvancedApply ? (
                    /* Advanced — role selector */
                    <div>
                      <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.5px', color: 'rgba(255,255,255,.5)', marginBottom: 10 }}>
                        SELECT A ROLE TO APPLY FOR
                      </p>
                      <div className="flex flex-col" style={{ gap: 9 }}>
                        {crewMembers.filter(m => !m.hired).map((member, idx) => {
                          const rate = member.ratePerHour || member.rate || 0;
                          const hours = member.hours || 0;
                          const sub = member.subtotal || rate * hours;
                          const payout = getCrewPayout(sub);
                          const isSelected = selectedApplyRole === member.role;
                          const rc = roleColor(member.role);
                          return (
                            <button
                              key={idx}
                              onClick={() => setSelectedApplyRole(member.role)}
                              className="text-left"
                              style={{
                                padding: 13, borderRadius: 13,
                                border: `1.5px solid ${isSelected ? '#78287C' : 'rgba(255,255,255,.12)'}`,
                                background: isSelected ? 'rgba(120,40,124,.18)' : 'rgba(255,255,255,.04)',
                              }}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                  <span
                                    className="flex items-center justify-center flex-shrink-0"
                                    style={{
                                      width: 20, height: 20, borderRadius: 999,
                                      border: `2px solid ${isSelected ? '#78287C' : 'rgba(255,255,255,.2)'}`,
                                      background: isSelected ? '#78287C' : 'transparent',
                                    }}
                                  >
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="inline-flex items-center gap-1.5" style={{ fontWeight: 600, fontSize: 14, color: '#fff' }}>
                                      <span style={{ width: 7, height: 7, borderRadius: 999, background: rc }} />
                                      {roleMeta(member.role).label}
                                    </p>
                                    <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)' }}>{hours}h @ {money(getCrewPayout(rate))}/hr</p>
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="cd-display" style={{ fontWeight: 800, fontSize: 16, color: '#67C6B4' }}>{money(payout)}</p>
                                  <p style={{ fontSize: 10.5, color: 'rgba(255,255,255,.5)' }}>payout</p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    /* Simple — payout summary */
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 14, background: 'rgba(103,198,180,.08)', border: '1px solid rgba(103,198,180,.22)' }}>
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.5px', color: '#67C6B4' }}>YOU EARN</div>
                        <div className="cd-display" style={{ fontWeight: 800, fontSize: 24, color: '#fff' }}>
                          {money(getCrewPayout(fullJobData.totalAmount || fullJobData.total_amount || fullJobData.total || 0))}
                        </div>
                      </div>
                      <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.6)' }} className="text-right">
                        {money(getCrewPayout(fullJobData.payRate || fullJobData.pay_rate || 0))}/hr · {fullJobData.hours || 0}h
                      </div>
                    </div>
                  )}

                  {/* Introduction message */}
                  <div>
                    <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, letterSpacing: '.5px', color: 'rgba(255,255,255,.5)', marginBottom: 8 }}>
                      INTRODUCE YOURSELF <span style={{ color: 'rgba(255,255,255,.35)', fontWeight: 500 }}>(optional)</span>
                    </label>
                    <textarea
                      value={applicationMessage}
                      onChange={(e) => setApplicationMessage(e.target.value)}
                      placeholder="Share why you're a great fit, your relevant experience, or any questions you have…"
                      rows={3}
                      className="cd-input"
                      style={{ resize: 'none', minHeight: 84, padding: '12px 14px', lineHeight: 1.5 }}
                    />
                  </div>

                  {/* Notify if unsuccessful */}
                  <label
                    className="flex items-start gap-3 cursor-pointer"
                    style={{
                      padding: 13, borderRadius: 13,
                      border: `1px solid ${notifyIfUnsuccessful ? '#78287C' : 'rgba(255,255,255,.12)'}`,
                      background: 'rgba(255,255,255,.04)',
                    }}
                  >
                    <span
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 20, height: 20, borderRadius: 6, marginTop: 1,
                        border: `2px solid ${notifyIfUnsuccessful ? '#78287C' : 'rgba(255,255,255,.2)'}`,
                        background: notifyIfUnsuccessful ? '#78287C' : 'transparent',
                      }}
                    >
                      {notifyIfUnsuccessful && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <input
                      type="checkbox"
                      checked={notifyIfUnsuccessful}
                      onChange={(e) => setNotifyIfUnsuccessful(e.target.checked)}
                      className="sr-only"
                    />
                    <span>
                      <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: '#fff' }}>
                        Notify me if I'm unsuccessful
                      </span>
                      <span style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,.55)', marginTop: 2 }}>
                        We'll email you if this role is filled or closed and you're not selected.
                      </span>
                    </span>
                  </label>
                </div>

                {/* Footer */}
                <div style={{ borderTop: '1px solid rgba(255,255,255,.1)', padding: '16px 26px', display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => { setShowApplyModal(false); setShowJobDetails(true); }}
                    disabled={submittingApplication}
                    className="cd-btn"
                    style={{ flex: 1, padding: 13, fontSize: 14 }}
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmitApplication}
                    disabled={submittingApplication}
                    className="cd-btn cd-btn-primary"
                    style={{ flex: 1.6, padding: 13, fontSize: 14 }}
                  >
                    {submittingApplication ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send application
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ===== Application Confirmation (inline modal) ===== */}
        {showConfirmationModal && (
          <div className="cd-modal-overlay">
            <div className="cd-modal-card" style={{ maxWidth: 420 }}>
              <div className="flex flex-col items-center text-center" style={{ padding: '36px 28px' }}>
                {/* Earn-tinted ring with checkmark */}
                <div
                  className="flex items-center justify-center mb-5"
                  style={{ width: 76, height: 76, borderRadius: 999, background: 'rgba(103,198,180,.14)' }}
                >
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#67C6B4" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="cd-display" style={{ fontWeight: 700, fontSize: 21, color: '#fff', letterSpacing: '-.4px', marginBottom: 8 }}>
                  Application submitted!
                </h3>
                <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,.6)', marginBottom: 22, maxWidth: '34ch' }}>
                  {fullJobData?.owner?.name || fullJobData?.owner_name
                    ? `${fullJobData.owner?.name || fullJobData.owner_name} will review your profile and may reach out soon.`
                    : 'The job owner will review your profile and may reach out soon.'}
                </p>
                <div className="flex w-full" style={{ gap: 10 }}>
                  <button
                    onClick={() => setShowConfirmationModal(false)}
                    className="cd-btn"
                    style={{ flex: 1 }}
                  >
                    Keep browsing
                  </button>
                  <button
                    onClick={handleCloseConfirmation}
                    className="cd-btn cd-btn-primary"
                    style={{ flex: 1 }}
                  >
                    View my applications
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profile Blocker Modal */}
        <ProfileBlockerModal
          isOpen={showProfileBlocker}
          onClose={() => setShowProfileBlocker(false)}
          userStatus={userStatus}
          userName={user.full_name || user.first_name || 'User'}
          userId={user.id}
        />

        {/* Cancellation Payment Blocker Modal */}
        <CancellationPaymentBlockerModal
          isOpen={showCancellationBlocker}
          outstandingFees={outstandingCancellationFees}
        />
      </div>
    </div>
  );
};

export default FindJobsRedesigned;
